import styled from 'styled-components';

import { Text } from '@/nova/components/Text/Text';
import { ANIM, COLORS } from '@/nova/theme/tokens';

export const Root = styled.div`
  position: relative;
  min-width: 0;
`;

/** The closed control: exactly the app-wide 48px so it lines up with the footer buttons. */
export const Trigger = styled.button`
  width: 100%;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 12px 0 16px;
  border-radius: 12px;
  border: 1px solid ${COLORS.panelBorder};
  background: ${COLORS.surfaceInset};
  color: ${COLORS.text};
  font-size: 16px;
  font-family: inherit;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const Value = styled(Text).attrs({ tag: 'span' as const })`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Chevron = styled.svg<{ $open: boolean }>`
  flex: 0 0 auto;
  width: 12px;
  height: 12px;
  fill: none;
  stroke: ${COLORS.textMuted};
  stroke-width: 1.6;
  transition: transform ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
  transform: rotate(${(p) => (p.$open ? '180deg' : '0deg')});
`;

/** The options list, opening UPWARD (`bottom: 100%`) so a footer dropdown never runs off the bottom of the
 *  viewport. Rounded + soft-elevated to match the rest of the studio's popovers. */
export const Menu = styled.div`
  position: absolute;
  bottom: calc(100% + 6px);
  left: 0;
  /* Grow the flyout to its content (so the rich model options get room to breathe instead of wrapping in the
     narrow control), but never below the trigger width and never past the viewport. A short list (aspect ratios)
     stays compact because max-content is small; only the wide model descriptions expand. */
  min-width: 100%;
  width: max-content;
  max-width: min(420px, calc(100vw - 32px));
  z-index: 30;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px;
  border-radius: 14px;
  background: ${COLORS.surfaceCard};
  border: 1px solid ${COLORS.panelBorder};
  box-shadow: ${COLORS.menuShadow};
  /* Tall enough that the current option lists fit WITHOUT a scrollbar; auto only scrolls a genuinely long
     list. Capped to the viewport so a big list opening upward never runs off the top. */
  max-height: min(60vh, 420px);
  overflow-y: auto;
`;

export const Option = styled.button<{ $selected: boolean; $rich?: boolean }>`
  display: flex;
  ${(p) => (p.$rich ? 'flex-direction: column; align-items: flex-start; gap: 1px;' : 'align-items: center;')}
  width: 100%;
  text-align: left;
  padding: 10px 12px;
  border: 0;
  border-radius: 10px;
  cursor: pointer;
  background: ${(p) => (p.$selected ? COLORS.ctaTint : 'transparent')};
  color: ${(p) => (p.$selected ? COLORS.ctaSolid : COLORS.text)};
  font-size: 14px;
  font-weight: ${(p) => (p.$selected ? 600 : 400)};
  font-family: inherit;
  transition: background ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    background: ${(p) => (p.$selected ? COLORS.ctaTint : COLORS.hoverWash)};
  }
`;

export const OptionText = styled(Text).attrs({ tag: 'span' as const })`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
